"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { nav } from "@/lib/site";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import amblem from "@/public/images/dpu-amblem.png";
import { useHeaderViewModel } from "./HeaderViewModel";

export default function Header() {
  const {
    open,
    setOpen,
    t,
    tNav,
    tSite,
    isActive,
    onNavClick,
    containerRef,
    indicatorStyle,
    mounted,
  } = useHeaderViewModel();

  return (
    <>
      <header className="sticky top-0 z-40 container-site pt-3">
        <div className="overflow-hidden rounded-2xl border border-white/60 bg-white/70 shadow-card-lg backdrop-blur-xl backdrop-saturate-150">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 sm:px-5">
            <Link href="/" className="flex min-w-0 items-center gap-3">
              <Image
                src={amblem}
                alt={t("logoAlt")}
                width={48}
                height={48}
                priority
                className="h-10 w-10 shrink-0 sm:h-11 sm:w-11"
              />
              <span className="min-w-0 leading-tight">
                <span className="block truncate font-serif text-base sm:text-lg font-semibold text-ink">
                  {tSite("shortName")}
                </span>
                <span className="block truncate text-[10px] sm:text-xs uppercase tracking-[0.14em] text-muted">
                  {tSite("kind")}
                </span>
              </span>
            </Link>

            <div className="flex shrink-0 items-center gap-3">
              <span className="hidden lg:block text-sm text-muted">
                {tSite("datesText")} · {tSite("city")}
              </span>
              <LanguageSwitcher />
              <Link
                href="/basvuru"
                className="hidden sm:inline-flex items-center rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
              >
                {t("submitPaper")}
              </Link>

              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-expanded={open}
                aria-controls="ana-menu"
                aria-label={t("openMenu")}
                className="md:hidden inline-flex items-center justify-center rounded-xl border border-line-strong p-2 text-ink transition-colors hover:border-brand hover:text-brand"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M3 6h18M3 12h18M3 18h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <nav
            aria-label={t("mainMenu")}
            className="hidden md:block border-t border-white/60"
          >
            <ul ref={containerRef} className="relative flex flex-nowrap items-center gap-1 overflow-x-auto px-3 py-2">
              {nav.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.href} className="shrink-0">
                    <Link
                      href={item.href}
                      onClick={() => onNavClick(item.href)}
                      aria-current={active ? "page" : undefined}
                      className={[
                        "relative z-10 inline-flex items-center whitespace-nowrap rounded-full px-3.5 py-1.5 text-[14px] font-medium transition-colors",
                        active
                          ? (mounted ? "text-ink/75" : "bg-brand text-white")
                          : "text-ink/75 hover:bg-white/70 hover:text-brand",
                      ].join(" ")}
                    >
                      {tNav(item.key)}
                    </Link>
                  </li>
                );
              })}
              {/*
                Kayan yeşil pill: clip-path ile kesilen bir üst katman.
                İçinde menünün birebir beyaz kopyası var; pill nereye kayarsa
                oradaki yazı beyaz görünür. Böylece yazı rengi pill'den önce
                ya da sonra değil, pill ile BİRLİKTE değişir.
              */}
              {mounted && indicatorStyle.total > 0 && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute left-0 top-0 z-20 h-full bg-brand transition-[clip-path,opacity] duration-300 ease-out"
                  style={{
                    width: indicatorStyle.total,
                    opacity: indicatorStyle.opacity,
                    clipPath: `inset(8px ${Math.max(
                      indicatorStyle.total -
                        indicatorStyle.left -
                        indicatorStyle.width,
                      0
                    )}px 8px ${indicatorStyle.left}px round 9999px)`,
                  }}
                >
                  <ul className="flex flex-nowrap items-center gap-1 px-3 py-2">
                    {nav.map((item) => (
                      <li key={item.href} className="shrink-0">
                        <span className="inline-flex items-center whitespace-nowrap px-3.5 py-1.5 text-[14px] font-medium text-white">
                          {tNav(item.key)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </ul>
          </nav>
        </div>
      </header>

      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        className={[
          "fixed inset-0 z-50 md:hidden",
          open ? "" : "pointer-events-none",
        ].join(" ")}
      />

      <nav
        id="ana-menu"
        data-open={open}
        aria-label={t("mainMenu")}
        className={[
          "fixed right-0 top-0 z-50 flex h-dvh w-[300px] max-w-[85vw] flex-col rounded-l-3xl border-l border-line bg-white shadow-card-lg transition-transform duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] md:hidden",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
            {t("menu")}
          </span>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("closeMenu")}
              className="inline-flex items-center justify-center rounded-xl border border-line-strong p-2 text-ink transition-colors hover:border-brand hover:text-brand"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <ul className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {nav.map((item) => {
            const active = isActive(item.href);
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  aria-current={active ? "page" : undefined}
                  className={[
                    "block rounded-xl px-4 py-3 text-[15px] transition-colors",
                    active
                      ? "bg-brand/10 font-semibold text-brand"
                      : "font-medium text-ink hover:bg-surface",
                  ].join(" ")}
                >
                  {tNav(item.key)}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="border-t border-line p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <Link
            href="/basvuru"
            onClick={() => setOpen(false)}
            className="block rounded-full bg-brand px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
          >
            {t("submitPaper")}
          </Link>
          <p className="mt-3 text-center text-xs text-muted">
            {tSite("datesText")} · {tSite("city")}
          </p>
        </div>
      </nav>
    </>
  );
}
